import $ from 'jquery';
import * as pagination from './pagination';
import * as loader from './loader';
import { buildUrl, searchQueryStringKeys, setSearchParam, getQueryText } from './search';

export const init = ($target) => {
	const $pressRelease = $('.js-press-release'),
		$searchBox = $pressRelease.find('.js-search-box'),
		$resultList = $pressRelease.find('.js-result-list');

	if (!$pressRelease.length || !$searchBox.length || !$resultList.length) {
		return;
	}

	///////////////////////////////////////////////////////////////////////////////
	/// CONSTANTS AND VARIABLES
	///////////////////////////////////////////////////////////////////////////////

	const API_BASE_URL = $searchBox.attr('action');

	const $queryTextInput = $searchBox.find('input[name=queryText]'),
		$loader = $('.js-loader');

	///////////////////////////////////////////////////////////////////////////////
	/// INITIALIZATION
	///////////////////////////////////////////////////////////////////////////////

	loader.show($loader);

	$target.ready(async function () {
		initParams(new URLSearchParams(window.location.search));

		attachCallbacks();

		onSubmit(true);
	});

	function initParams(urlSearchParams) {
		const queryText = urlSearchParams.get(searchQueryStringKeys.queryText);

		setQueryInput(queryText ?? '');
	}

	function attachCallbacks() {
		initPagination();

		$searchBox.on('submit', function (event) {
			event.preventDefault();

			onSubmit(false, 1);
		});

		window.onpopstate = function (event) {
			initParams(new URLSearchParams(event.target.location.search));

			onSubmit(true);
		};
	}

	function initPagination() {
		pagination.init('.js-press-release', onPaginationChange);

		function onPaginationChange(page) {
			onSubmit(false, page);
		}
	}

	///////////////////////////////////////////////////////////////////////////////
	/// SEARCH EVENTS
	///////////////////////////////////////////////////////////////////////////////

	async function onSubmit(queryUpdateReplaceState = false, page = null) {
		const params = getParams();

		if (page) params.page = page;

		$queryTextInput.trigger('blur');
		loader.show($loader);

		updateQueryString(params, queryUpdateReplaceState);

		let response = '';
		try {
			response = await search(params);
		} catch (err) {
			if (err.status !== '404') console.error('There seems to be an error with the API');
		}

		// try resubmitting with page 1
		if (response.totalCount > 0 && response.pageCount > 0 && !response.results.length) {
			onSubmit(true, 1);
			return;
		}

		fillSearchResults(response);
		loader.hide($loader);
		pagination.update(response.pageIndex, response.pageCount);

		try {
			pushDataLayer(params, response);
		} catch (err) {
			console.error('Could not push data layer: ' + err);
		}
	}

	function search(params) {
		let url = `${API_BASE_URL}/${params.segment}`;

		const urlSearchParams = new URLSearchParams();
		setSearchParam(urlSearchParams, searchQueryStringKeys.page, params.page);
		setSearchParam(urlSearchParams, searchQueryStringKeys.queryText, params.queryText);
		setSearchParam(urlSearchParams, searchQueryStringKeys.categories, params.categories);
		setSearchParam(urlSearchParams, searchQueryStringKeys.childOnly, params.childOnly);

		url = buildUrl(url, urlSearchParams);

		return $.ajax({
			type: 'GET',
			url: url,
			contentType: 'text/plain',
		});
	}

	function pushDataLayer(params, response) {
		if (!response || !response.results) {
			return;
		}

		const dataLayerObject = {
			event: 'internalSearchResult',
			internalSearchType: $searchBox.attr('id'),
			internalSearchKeyword: params.queryText,
			internalSearchResultCount: response.results.length,
			internalSearchFilters: {
				segments: [params.segment],
				types: [params.categories],
			},
		};

		dataLayer.push(dataLayerObject);
	}

	///////////////////////////////////////////////////////////////////////////////
	/// UTILS
	///////////////////////////////////////////////////////////////////////////////

	function updateQueryString(params, replaceState = false) {
		const urlSearchParams = new URLSearchParams(window.location.search);
		const oldQueryString = urlSearchParams.toString();

		setSearchParam(urlSearchParams, searchQueryStringKeys.page, params.page);
		setSearchParam(urlSearchParams, searchQueryStringKeys.queryText, params.queryText);

		const newQueryString = urlSearchParams.toString();

		if (oldQueryString === newQueryString) {
			return;
		}

		if (replaceState) {
			window.history.replaceState(null, '', '?' + newQueryString);
		} else {
			window.history.pushState(null, '', '?' + newQueryString);
		}
	}

	function fillSearchResults(response) {
		let resultListHtml = '';

		if (!response || !response.results || !response.results.length) {
			resultListHtml = getNoResultsItemHtml();
			pagination.hide();
		} else {
			resultListHtml = getResultListHtml(response.results);
			pagination.show();
		}

		$resultList.html(resultListHtml);
	}

	function getParams() {
		return {
			queryText: getQueryText($queryTextInput),
			segment: $searchBox.find('input[name=segment]').val(),
			categories: $searchBox.find('input[name=categories]').val(),
			page: new URLSearchParams(window.location.search).get('page') ?? 1,
			childOnly: !!$searchBox.find('input[name=childOnly]').val(),
		};
	}

	function setQueryInput(queryText) {
		$queryTextInput.val(queryText.trim());
	}

	const getResultListHtml = (results) => results.map((item) => getItemHtml(item)).join('');

	const getNoResultsItemHtml = () => {
		const text = $resultList.data('no-results-text');
		return `
            <div class="press-release__no-result">
                ${text}
            </div>`;
	};

	const getItemHtml = (item) => {
		const parseDate = (dateStr) => {
			if (!dateStr) {
				return '';
			}

			const dateParts = dateStr.split('. ');

			let datetime = dateParts[2];
			datetime += '-';
			datetime += dateParts[1].length === 1 ? '0' + dateParts[1] : dateParts[1];
			datetime += '-';
			datetime += dateParts[0].length === 1 ? '0' + dateParts[0] : dateParts[0];

			return datetime;
		};

		const datetime = parseDate(item.publishFrom);

		return `
            <div class="press-release__item">
                <a href="${item.link}" class="press-release__link">
                    <time class="press-release__time" datetime="${datetime}">${item.publishFrom ?? ''}</time>
                    <span class="item-icon item-icon--after item-icon--space-between">
                        <span class="item-icon__icon">${arrowRight}</span>
                        <span class="item-icon__text">${item.pageName}</span>
                    </span>
                </a>
            </div>`;
	};

	const arrowRight = `
        <span class="icon-svg icon-svg--arrow-right" aria-hidden="true">
            <svg class="icon-svg__svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <use xlink:href="/img/bg/icons-svg.svg#icon-arrow-right" width="100%" height="100%" focusable="false"></use>
            </svg>
        </span>`;
};
